import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { X } from 'lucide-react';
import { toast } from 'sonner';

// Social platform configurations
const PLATFORMS = [
  {
    id: 'twitter',
    name: 'X (Twitter)',
    icon: '𝕏',
    color: 'hover:bg-black hover:text-white',
    getUrl: (permalink, title) => 
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(permalink)}`
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: 'f',
    color: 'hover:bg-blue-600 hover:text-white',
    getUrl: (permalink) => 
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(permalink)}`
  },
  {
    id: 'reddit',
    name: 'Reddit',
    icon: '𝗿',
    color: 'hover:bg-orange-600 hover:text-white',
    getUrl: (permalink, title) => 
      `https://www.reddit.com/submit?url=${encodeURIComponent(permalink)}&title=${encodeURIComponent(title)}`
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    icon: 'in',
    color: 'hover:bg-blue-700 hover:text-white',
    getUrl: (permalink) => 
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(permalink)}`
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    icon: '💬',
    color: 'hover:bg-green-600 hover:text-white',
    mobileOnly: true,
    getUrl: (permalink, title) => 
      `https://api.whatsapp.com/send?text=${encodeURIComponent(title)}%20${encodeURIComponent(permalink)}`
  },
  {
    id: 'telegram',
    name: 'Telegram',
    icon: '✈️',
    color: 'hover:bg-blue-500 hover:text-white',
    getUrl: (permalink, title) => 
      `https://t.me/share/url?url=${encodeURIComponent(permalink)}&text=${encodeURIComponent(title)}`
  }
];

const ShareSheet = ({ isOpen, onClose, permalink, postContent, onShare }) => {
  // Generate title/snippet from post content (first 100 chars)
  const getShareTitle = () => {
    if (!postContent || !postContent.trim()) {
      return "Check out this post on Trap Comix";
    }
    const text = postContent.replace(/<[^>]*>/g, '').trim(); // Strip HTML
    return text.length > 100 ? text.substring(0, 100) + '...' : text;
  };

  const title = getShareTitle();
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const handlePlatformClick = (platform) => {
    const url = platform.getUrl(permalink, title);
    
    // Fire analytics event
    if (onShare) {
      onShare(platform.id);
    }
    
    // Show toast
    toast.info(`Opening ${platform.name}...`);
    
    // Open in new window
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=600');
    
    // Close sheet
    onClose();
  };

  // Filter platforms (hide mobile-only on desktop)
  const visiblePlatforms = PLATFORMS.filter(p => !p.mobileOnly || isMobile);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-800 max-w-md">
        <DialogHeader>
          <DialogTitle>Share Post</DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-2 gap-3 py-4">
          {visiblePlatforms.map(platform => (
            <Button
              key={platform.id}
              onClick={() => handlePlatformClick(platform)}
              variant="outline"
              className={`h-16 flex flex-col items-center justify-center gap-1 border-gray-700 ${platform.color} transition-colors`}
              aria-label={`Share to ${platform.name}`}
            >
              <span className="text-2xl">{platform.icon}</span>
              <span className="text-xs">{platform.name}</span>
            </Button>
          ))}
        </div>
        
        <div className="text-xs text-gray-400 text-center">
          Share link: {permalink}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareSheet;
